import { useState, useEffect } from "react";
import { Info } from "lucide-react";
import { toast } from "react-toastify";
import { getAdmins } from "./supportApi";

const formatDate = (date) => {
  if (!date) return "N/A";
  return new Date(date).toLocaleString("en-US");
};

/**
 * Read-only on purpose. Granting and revoking admin access needs the help center's API
 * key, which must never reach a browser, so there is no add or remove UI here.
 */
export default function AdminAccess() {
  const [admins, setAdmins] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchAdmins = async () => {
      try {
        const data = await getAdmins(1, 50);
        setAdmins(data.admins || []);
        setTotal(data.total ?? (data.admins || []).length);
      } catch (error) {
        console.error("Error fetching admins:", error);
        toast.error("Failed to load the admin list");
      } finally {
        setIsLoading(false);
      }
    };
    fetchAdmins();
  }, []);

  return (
    <div className="">
      {/* Page header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
          <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
          Support
        </div>
        <h1 className="font-serif italic text-[36px] leading-[1.05] text-foreground mb-1">
          Admin <span className="text-gradient-ginox">Access</span>
        </h1>
        <p className="text-sm text-muted-foreground mb-4">
          Everyone who can sign in to this panel
        </p>
      </div>

      <div className="max-w-4xl space-y-5">
        <div className="flex items-start gap-2.5 rounded-lg border border-blue-500/20 bg-blue-500/10 p-4">
          <Info
            className="h-4 w-4 text-blue-400 mt-0.5 shrink-0"
            aria-hidden="true"
          />
          <p className="text-xs leading-relaxed text-blue-300">
            Access is granted and revoked outside this panel — it needs the help
            center's API key, which must never be sent from a browser. Ask a backend
            admin to add or remove someone. Removing an admin takes effect on their
            very next request.
          </p>
        </div>

        {isLoading ? (
          <div className="py-10 flex justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-ginox-green" />
          </div>
        ) : admins.length === 0 ? (
          <p className="text-muted-foreground text-sm">No admins found.</p>
        ) : (
          <>
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
              {total} {total === 1 ? "admin" : "admins"}
            </p>
            <div className="overflow-x-auto">
              <table className="w-full border-separate border-spacing-0 rounded-lg border border-border/70 bg-card overflow-hidden">
                <thead>
                  <tr>
                    <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                      Name
                    </th>
                    <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                      Admin ID
                    </th>
                    <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                      Added
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {admins.map((admin) => (
                    <tr
                      key={admin.id}
                      className="hover:bg-white/[0.04] transition"
                    >
                      <td className="border-b border-border/40 px-4 py-3 text-sm text-foreground">
                        {admin.name}
                      </td>
                      <td className="border-b border-border/40 px-4 py-3 text-sm font-mono text-muted-foreground">
                        {admin.adminId}
                      </td>
                      <td className="border-b border-border/40 px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                        {formatDate(admin.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
